const SCHEMA_CONTEXT = 'https://schema.org';
const DEFAULT_SITE_NAME = 'SevenSix';

function getSsrHost(vm) {
    const headers = vm && vm.$ssrContext && vm.$ssrContext.req ? vm.$ssrContext.req.headers : null;

    if (!headers) {
        return null;
    }

    return headers['x-forwarded-host'] || headers.host || null;
}

function getClientHost() {
    if (process.client && typeof window !== 'undefined' && window.location) {
        return window.location.host || null;
    }

    return null;
}

function getProtocol(vm) {
    if (process.client && typeof window !== 'undefined' && window.location && window.location.protocol) {
        return window.location.protocol.replace(':', '');
    }

    if (vm && vm.$config && vm.$config.siteProtocol) {
        return vm.$config.siteProtocol;
    }

    return process.env.HTTPS === 'on' ? 'https' : 'http';
}

export function getSiteHost(vm) {
    return getSsrHost(vm) || getClientHost() || process.env.DOMAIN_NAME_CLIENTS || null;
}

export function getSiteName(vm) {
    return (vm && vm.$config && vm.$config.siteName) || DEFAULT_SITE_NAME;
}

export function getSiteCurrency(vm) {
    const siteCountry = vm && vm.$config ? vm.$config.siteCountry : null;

    return siteCountry === 'kz' ? 'KZT' : 'RUB';
}

export function getAboutContactData(vm) {
    const siteCountry = vm && vm.$config ? vm.$config.siteCountry : null;

    if (siteCountry === 'kz') {
        return {
            organizationName: 'Seven six Kazakhstan',
            email: 'info@sevensix.kz',
            phones: ['+77077750076'],
            contactType: 'customer support',
            pickupAddress: {
                streetAddress: 'ул. Достык, 18, 4 этаж',
                addressLocality: 'Астана',
                addressCountry: 'KZ',
            },
            legalAddress: {
                streetAddress: 'улица Шамши Калдаякова, дом 17, кв. 342',
                addressLocality: 'Астана',
                postalCode: '010000',
                addressCountry: 'KZ',
            },
        };
    }

    return {
        organizationName: 'ООО «Севен Сикс»',
        email: 'info@sevensix.sport',
        phones: ['+78001019675', '+79024435476'],
        contactType: 'customer support',
        availableLanguage: ['ru'],
        openingHours: ['Mo-Fr 09:00-17:00'],
        legalAddress: {
            streetAddress: 'улица Академика Сахарова, 29, помещение 21',
            addressLocality: 'Екатеринбург',
            addressRegion: 'Свердловская область',
            postalCode: '620105',
            addressCountry: 'RU',
        },
    };
}

export function getAbsoluteUrl(vm, path = '/') {
    const host = getSiteHost(vm);
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    if (!host) {
        return normalizedPath;
    }

    return `${getProtocol(vm)}://${host}${normalizedPath}`;
}

export function getCanonicalUrl(vm) {
    const path = vm && vm.$route && vm.$route.path ? vm.$route.path : '/';

    return getAbsoluteUrl(vm, path);
}

export function getCurrentUrl(vm) {
    const fullPath = vm && vm.$route && vm.$route.fullPath ? vm.$route.fullPath : (vm && vm.$route && vm.$route.path ? vm.$route.path : '/');

    return getAbsoluteUrl(vm, fullPath);
}

export function stripHtml(value) {
    return String(value || '')
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();
}

export function getAbsoluteImageUrl(vm, imageUrl) {
    if (!imageUrl) {
        return null;
    }

    if (/^https?:\/\//i.test(imageUrl)) {
        return imageUrl;
    }

    return getAbsoluteUrl(vm, imageUrl);
}

export function createBreadcrumbSchema(vm, items, id = '#breadcrumbs') {
    const itemListElement = (Array.isArray(items) ? items : [])
        .filter(item => item && item.name)
        .map((item, index) => {
            const listItem = {
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
            };

            if (item.path) {
                listItem.item = getAbsoluteUrl(vm, item.path);
            }

            return listItem;
        });

    if (!itemListElement.length) {
        return null;
    }

    return {
        '@type': 'BreadcrumbList',
        '@id': `${getCanonicalUrl(vm)}${id}`,
        itemListElement,
    };
}

export function createItemListSchema(vm, items, id = '#item-list') {
    const itemListElement = (Array.isArray(items) ? items : [])
        .filter(item => item && item.url && item.name)
        .map((item, index) => {
            const listItem = {
                '@type': 'ListItem',
                position: index + 1,
                url: item.url,
                name: item.name,
            };

            if (item.image || item.price) {
                const product = {
                    '@type': 'Product',
                    name: item.name,
                    url: item.url,
                };

                if (item.image) {
                    product.image = item.image;
                }

                if (item.price) {
                    product.offers = {
                        '@type': 'Offer',
                        price: item.price,
                        priceCurrency: item.currency || getSiteCurrency(vm),
                        availability: item.available === false
                            ? 'https://schema.org/OutOfStock'
                            : 'https://schema.org/InStock',
                        url: item.url,
                    };
                }

                listItem.item = product;
            }

            return listItem;
        });

    if (!itemListElement.length) {
        return null;
    }

    return {
        '@type': 'ItemList',
        '@id': `${getCanonicalUrl(vm)}${id}`,
        itemListElement,
    };
}

export function createSchemaHead(graph, hid = 'schema-org') {
    const nodes = (Array.isArray(graph) ? graph : []).filter(Boolean);

    if (!nodes.length) {
        return {};
    }

    return {
        script: [
            {
                hid,
                type: 'application/ld+json',
                innerHTML: JSON.stringify({
                    '@context': SCHEMA_CONTEXT,
                    '@graph': nodes,
                }),
            },
        ],
        __dangerouslyDisableSanitizersByTagID: {
            [hid]: ['innerHTML'],
        },
    };
}

export function createPostalAddress(address) {
    if (!address) {
        return null;
    }

    return {
        '@type': 'PostalAddress',
        streetAddress: address.streetAddress || undefined,
        addressLocality: address.addressLocality || undefined,
        addressRegion: address.addressRegion || undefined,
        postalCode: address.postalCode || undefined,
        addressCountry: address.addressCountry || undefined,
    };
}
